import { Button, Card, CardContent, CardTitle, Separator } from '@repo/shadcn';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

import { SectionContainer } from '@components/shared';
import { HeaderBreadcrumb, SectionTitle } from '@components/ui';

import { Route } from '@/constants';
import {
    PurchaseReturnDetailsProvider,
    usePurchaseReturnDetailsContext,
} from '@/modules/purchase/purchase-return/purchase-return-details/purchase-return-details.context';
import { formatCurrencyInBDT } from '@/utils';

const PurchaseReturnDetailsComponent = () => {
    const { data, isError, isLoading, error } =
        usePurchaseReturnDetailsContext();

    const total = data?.data?.items?.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0,
    );

    const renderItems = () => {
        if (isLoading) {
            return <div>Loading items...</div>;
        }

        if (isError) {
            return <div>Something went wrong!</div>;
        }

        return data?.data?.items.map((item) => {
            return (
                <div key={item.id} className="grid grid-cols-10 gap-4 text-sm">
                    <div className="col-span-4 font-medium">
                        {item.product_name}
                    </div>
                    <div className="col-span-2 text-right">
                        {formatCurrencyInBDT(item.price)}
                    </div>
                    <div className="col-span-2 text-right">{item.quantity}</div>
                    <div className="col-span-2 text-right font-medium">
                        {formatCurrencyInBDT(item.price * item.quantity)}
                    </div>
                </div>
            );
        });
    };
    return (
        <SectionContainer
            error={error}
            isError={isError}
            isLoading={isLoading}
            fallbackPath={Route.PURCHASE_RETURNS}
        >
            <Card className={'space-y-4 p-4'}>
                <HeaderBreadcrumb
                    items={[
                        {
                            label: 'Purchases',
                            href: Route.PURCHASES,
                        },
                        {
                            label: 'Purchase Returns',
                            href: Route.PURCHASE_RETURNS,
                        },
                        { label: `Return Details` },
                    ]}
                />

                <SectionTitle>Purchase Return</SectionTitle>

                <div className={'grid grid-cols-[2fr_1fr] gap-4'}>
                    <Card className={'p-4'}>
                        <CardTitle className="text-lg">Return Items</CardTitle>

                        <CardContent className={'p-0'}>
                            <div className="space-y-4">
                                <div className="grid grid-cols-10 gap-4 text-sm font-semibold text-muted-foreground">
                                    <div className="col-span-4">Name</div>
                                    <div className="col-span-2 text-right">
                                        Price
                                    </div>
                                    <div className="col-span-2 text-right">
                                        Quantity
                                    </div>
                                    <div className="col-span-2 text-right">
                                        Total
                                    </div>
                                </div>
                                <Separator />
                                {renderItems()}
                            </div>

                            <Separator className={'my-4'} />

                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between border-t pt-3">
                                    <span className="font-semibold">
                                        Total:
                                    </span>
                                    <span className="text-lg font-bold">
                                        {formatCurrencyInBDT(total ?? 0)}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className={'p-4'}>
                        <CardTitle className="text-lg">Details</CardTitle>

                        <CardContent className={'p-0'}>
                            <div className={'flex justify-between'}>
                                <span>Return Date: </span>
                                <span>
                                    {data?.data?.created_at
                                        ? format(
                                              data?.data?.created_at,
                                              'dd/MM/yyyy HH:mm a',
                                          )
                                        : '---'}
                                </span>
                            </div>

                            <div className={'flex justify-between'}>
                                <span>Invoice: </span>
                                <Link
                                    to={`${Route.PURCHASES}/${data?.data?.purchase_id}`}
                                >
                                    <Button
                                        type={'button'}
                                        variant={'link'}
                                        size={'sm'}
                                        className={'p-0'}
                                    >
                                        {data?.data?.purchase?.invoice_number
                                            ? data?.data?.purchase
                                                  ?.invoice_number
                                            : '---'}
                                    </Button>
                                </Link>
                            </div>

                            <div className={'flex justify-between'}>
                                <span>Processed By: </span>
                                <Link
                                    to={`${Route.USERS}/${data?.data?.user_id}`}
                                >
                                    <Button
                                        type={'button'}
                                        variant={'link'}
                                        size={'sm'}
                                        className={'p-0'}
                                    >
                                        {data?.data?.user
                                            ? data?.data?.user?.name
                                            : '---'}
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </Card>
        </SectionContainer>
    );
};

export const PurchaseReturnDetails = () => {
    return (
        <PurchaseReturnDetailsProvider>
            <PurchaseReturnDetailsComponent />
        </PurchaseReturnDetailsProvider>
    );
};

PurchaseReturnDetails.displayName = 'PurchaseReturnDetails';
